import React, { useEffect, useMemo, useRef, useState } from "react";
import { AutoComplete, Input, Tag, Tooltip, Typography, theme } from "antd";
import { QuestionCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { buscarEnTodo } from "../../services/busquedaGlobal";
import { buscarEnElMenu } from "../../config/menu/buscadorDelMenu";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { anunciarBusqueda } from "../../hooks/busquedaDesdeLaCabecera";

const { Text } = Typography;

/**
 * Buscar en todo el terminal desde la cabecera.
 *
 * Contesta «¿dónde está esto?»: se escribe un nombre y salen las fichas que se
 * llaman así, con el maestro al que pertenecen. Hasta ahora había que
 * adivinarlo --abrir Clientes a mirar, luego Tiendas, luego Productos-- porque
 * cada listado sólo busca dentro de sí mismo.
 *
 * Al elegir una ficha se va a su pantalla **con la búsqueda ya puesta**, no a
 * la ficha suelta. Es a propósito: casi nunca se busca un registro para verlo y
 * ya, sino para trabajar con él, y el listado es donde están sus acciones. El
 * texto viaja en la dirección (`?q=`) y lo recoge la barra de herramientas
 * común, así que vale para los dieciséis listados sin tocar ninguno.
 */
const BuscadorGlobal = ({ compacto = false, pantallas = [] }) => {
  const { token } = theme.useToken();
  const navegar = useNavigate();
  const [texto, setTexto] = useState("");
  const [grupos, setGrupos] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const buscado = useDebouncedValue(texto, 250);
  const corridaVigente = useRef(0);
  const campo = useRef(null);

  // Ctrl+K abre **éste**, no el de la barra lateral.
  //
  // Al principio la tecla estaba en el buscador de pantallas, y era el sitio
  // equivocado: en cualquier programa que la traiga aprendida --el editor, el
  // chat-- Ctrl+K abre el buscador grande, el que busca datos. Quien la pulsara
  // esperando un cliente se encontraba el menú.
  //
  // Y para no dejar huérfano al otro, éste busca también pantallas: hay un solo
  // sitio al que ir y encuentra las dos cosas.
  useEffect(() => {
    const alPulsar = (evento) => {
      if (evento.key !== "k" || !(evento.ctrlKey || evento.metaKey) || evento.altKey) return;
      evento.preventDefault();
      campo.current?.focus({ cursor: "all" });
    };
    document.addEventListener("keydown", alPulsar);
    return () => document.removeEventListener("keydown", alPulsar);
  }, []);

  useEffect(() => {
    // Cada búsqueda lleva número: las respuestas de una base local no llegan
    // necesariamente en el orden en que se pidieron, y sin esto una consulta
    // vieja puede pisar el resultado de la que se está escribiendo.
    const corrida = ++corridaVigente.current;
    if (buscado.trim().length < 2) {
      setGrupos([]);
      setBuscando(false);
      return;
    }

    setBuscando(true);
    buscarEnTodo(buscado)
      .then((encontrados) => {
        if (corrida === corridaVigente.current) setGrupos(encontrados);
      })
      .finally(() => {
        if (corrida === corridaVigente.current) setBuscando(false);
      });
  }, [buscado]);

  // Se va al listado con la búsqueda puesta **y** con el código de la ficha
  // elegida, que es lo que señala su renglón. El texto solo no basta: si tres
  // clientes se llaman parecido, cuál era el que se pulsó se perdía.
  const ir = (ruta, codigo) => {
    setTexto("");
    setGrupos([]);
    const texto = buscado.trim();
    const parametros = new URLSearchParams({ q: texto });
    if (codigo) parametros.set("fila", codigo);
    navegar(`${ruta}?${parametros}`);

    // Y se anuncia, para la pantalla que ya estaba montada: buscar un cliente
    // desde Clientes navega a la misma ruta, y las rutas llevan `key` fija, así
    // que no se vuelve a montar y no leería la dirección nueva. Va en el
    // siguiente turno para que la pantalla de destino ya exista.
    setTimeout(() => anunciarBusqueda(texto, codigo), 0);
  };

  // Las pantallas van primero: se llega a ellas de un salto y son pocas, así
  // que no tapan las fichas. Salen del mismo buscador que usa la barra lateral.
  const gruposConPantallas = useMemo(() => {
    const encontradas = buscarEnElMenu(pantallas, buscado);
    if (!encontradas.length) return grupos;
    return [
      {
        tipo: "Pantalla",
        ruta: null,
        total: encontradas.length,
        fichas: encontradas.slice(0, 5).map((p) => ({
          titulo: p.titulo,
          codigo: p.padre || "",
          ruta: p.destino,
        })),
      },
      ...grupos,
    ];
  }, [grupos, pantallas, buscado]);

  const opciones = useMemo(
    () =>
      gruposConPantallas.map((grupo) => ({
        label: (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase" }}>
              {grupo.tipo}
            </Text>
            {grupo.total > grupo.fichas.length && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {grupo.total} en total
              </Text>
            )}
          </div>
        ),
        options: grupo.fichas.map((ficha, indice) => ({
          value: `${grupo.tipo}-${ficha.codigo}-${indice}`,
          ruta: ficha.ruta || grupo.ruta,
          codigo: ficha.ruta ? "" : ficha.codigo,
          label: (
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {ficha.titulo}
              </span>
              {ficha.codigo && ficha.codigo !== ficha.titulo && (
                <Tag style={{ margin: 0, fontSize: 11 }}>{ficha.codigo}</Tag>
              )}
            </div>
          ),
        })),
      })),
    [gruposConPantallas]
  );

  const sinResultados = buscado.trim().length >= 2 && !buscando && grupos.length === 0;

  return (
    <AutoComplete
      value={texto}
      options={opciones}
      onSearch={setTexto}
      onChange={setTexto}
      onSelect={(_valor, opcion) => ir(opcion.ruta, opcion.codigo)}
      popupMatchSelectWidth={compacto ? 280 : 420}
      style={{ width: "100%", maxWidth: compacto ? 220 : 420 }}
      notFoundContent={
        sinResultados ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Nada se llama así en este equipo
          </Text>
        ) : null
      }
    >
      <Input
        ref={campo}
        allowClear
        prefix={<SearchOutlined style={{ color: token.colorTextQuaternary }} />}
        // Este buscador no busca como el de un listado, y conviene decirlo: los
        // listados miran muchos campos --ciudad, teléfono, estado--, y éste sólo
        // el nombre y el código. Sin avisar, quien busque un teléfono aquí
        // concluirá que el buscador no funciona.
        suffix={
          <Tooltip
            title={
              <>
                <div style={{ marginBottom: 4 }}>
                  Busca por <b>nombre</b> y <b>código</b> en:
                </div>
                clientes · productos · tiendas · cajas · colegios · usuarios · vendedores · turnos ·
                formas de pago · cupones · campañas · documentos
                <div style={{ marginTop: 6, opacity: 0.75 }}>
                  Dentro de cada pantalla, su propio buscador mira más campos.
                </div>
              </>
            }
          >
            <QuestionCircleOutlined style={{ cursor: "help", opacity: 0.45 }} />
          </Tooltip>
        }
        placeholder="Buscar en todo"
        aria-label="Buscar clientes, productos, tiendas y demás"
      />
    </AutoComplete>
  );
};

export default BuscadorGlobal;
